import { useEffect, useState } from "react";
import { useIsVerified } from "src/components/email-verification";
import {
  MainCard,
  MainCardContent,
  MainCardTitle,
} from "src/components/main-card-type.tsx";
import { Button } from "src/components/ui/button";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  sanitizeBrlValue,
  saveCalculatorData,
} from "src/utils/functions";
import { calcularRotativo, parseNumericValue } from "../utils/RotativoFunctions";
import { 
  RotativoInputData, 
  RotativoResultsType, 
  CONSTANTES_ROTATIVO 
} from "../calculadora-rotativo.d";

const CALCULATOR_KEY = "calculadora-juros-rotativo";

const DEFAULT_VALUES: RotativoInputData = {
  valorFatura: "",
  valorPago: "",
  taxaJuros: "13.9",
  diasAtraso: "0",
  multaAtraso: "2",
  jurosMora: "1",
  numeroMeses: "1",
} as const;

export const RotativoInputCard = ({
  setResults,
}: {
  setResults: (value: RotativoResultsType | null) => void;
}) => {
  const [message, setMessage] = useState<string>("");
  const [formData, setFormData] = useState<RotativoInputData>(DEFAULT_VALUES);
  const [isCalculating, setIsCalculating] = useState(false);

  const isVerified = useIsVerified();

  const updateFormField = <K extends keyof RotativoInputData>(field: K, value: RotativoInputData[K]) => {
    setFormData((prev: RotativoInputData) => ({ ...prev, [field]: value }));
  };

  const validateForm = (): string | null => {
    if (!formData.valorFatura) return "Informe o valor total da fatura.";
    
    const valorFaturaNum = parseNumericValue(formData.valorFatura);
    if (valorFaturaNum <= 0) return "Valor da fatura deve ser maior que zero.";
    
    const valorPagoNum = parseNumericValue(formData.valorPago);
    if (valorPagoNum > valorFaturaNum) {
      return "Valor pago não pode ser maior que o valor da fatura.";
    }
    
    if (!formData.taxaJuros) return "Informe a taxa de juros.";
    
    const taxaNum = parseNumericValue(formData.taxaJuros);
    if (taxaNum <= 0) return "Taxa de juros deve ser maior que zero.";
    
    return null;
  };

  const executeCalculation = async () => {
    const resultado = calcularRotativo(
      formData.valorFatura,
      formData.valorPago,
      formData.taxaJuros,
      formData.diasAtraso,
      formData.multaAtraso,
      formData.jurosMora
    );

    setResults(resultado);
    setMessage("");
  };

  const calcular = async () => {
    const validationError = validateForm();
    if (validationError) {
      setMessage(validationError);
      return;
    }

    setIsCalculating(true);
    setMessage("");

    try {
      await executeCalculation();
    } catch (error) {
      console.error('Erro no cálculo:', error);
      setMessage((error as Error).message || "Erro no cálculo. Verifique os dados informados.");
      setResults(null);
    } finally {
      setIsCalculating(false);
    }
  };

  const clear = () => {
    setFormData(DEFAULT_VALUES);
    setResults(null);
    setMessage("");
  };

  const calcularMinimo = () => {
    const valorFatura = parseNumericValue(formData.valorFatura);
    if (valorFatura > 0) {
      const minimo = valorFatura * CONSTANTES_ROTATIVO.PERCENTUAL_MINIMO;
      updateFormField('valorPago', minimo.toFixed(2).replace('.', ','));
    }
  };

  const handleSave = () => {
    saveCalculatorData(CALCULATOR_KEY, {
      valorFatura: formData.valorFatura,
      valorPago: formData.valorPago,
      taxaJuros: formData.taxaJuros,
      diasAtraso: formData.diasAtraso,
      multaAtraso: formData.multaAtraso,
      jurosMora: formData.jurosMora,
      numeroMeses: formData.numeroMeses,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
    checkCalculatorUsageLimitAndRedirect(calcular);
  };

  useEffect(() => {
    const savedData = loadSavedCalculatorData(CALCULATOR_KEY);
    if (savedData) {
      setFormData({
        valorFatura: (savedData.valorFatura as string) || DEFAULT_VALUES.valorFatura,
        valorPago: (savedData.valorPago as string) || DEFAULT_VALUES.valorPago,
        taxaJuros: (savedData.taxaJuros as string) || DEFAULT_VALUES.taxaJuros,
        diasAtraso: (savedData.diasAtraso as string) || DEFAULT_VALUES.diasAtraso,
        multaAtraso: (savedData.multaAtraso as string) || DEFAULT_VALUES.multaAtraso,
        jurosMora: (savedData.jurosMora as string) || DEFAULT_VALUES.jurosMora,
        numeroMeses: (savedData.numeroMeses as string) || DEFAULT_VALUES.numeroMeses,
      });
    }
  }, []);

  return (
    <MainCard>
      <MainCardTitle>Calculadora de Juros Rotativo do Cartão</MainCardTitle>
      <form onSubmit={handleSubmit}>
        <MainCardContent>
          <div>
            <label className="block mb-2 font-medium text-sm" htmlFor="valor-fatura">
              Valor Total da Fatura*
            </label>
            <div className="flex border">
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                R$
              </span>
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="valor-fatura"
                placeholder="1.000,00"
                type="text"
                value={formData.valorFatura}
                onChange={(event) => updateFormField('valorFatura', sanitizeBrlValue(event))}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2 font-medium text-sm" htmlFor="valor-pago">
                Valor que consegue pagar *
              </label>
              <div className="flex border">
                <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                  R$
                </span>
                <input
                  className="flex-1 p-2 min-w-[100px]"
                  id="valor-pago"
                  placeholder="150,00"
                  type="text"
                  value={formData.valorPago}
                  onChange={(event) => updateFormField('valorPago', sanitizeBrlValue(event))}
                />
                <button
                  type="button"
                  onClick={calcularMinimo}
                  className="px-3 py-1 text-xs bg-red-100 hover:bg-red-200 border-l transition-colors"
                  title="Calcular valor mínimo (15%)"
                >
                  Min. 15%
                </button>
              </div>
            </div>
            
            <div>
              <label className="block mb-2 font-medium text-sm" htmlFor="taxa-juros">
                Taxa de Juros Rotativo (ao mês) *
              </label>
              <div className="flex border">
                <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                  %
                </span>
                <input
                  className="flex-1 p-2 min-w-[100px]"
                  id="taxa-juros"
                  placeholder="13,9"
                  type="text"
                  value={formData.taxaJuros}
                  onChange={(event) => updateFormField('taxaJuros', sanitizeBrlValue(event))}
                />
              </div>
            </div>
          </div>

 <div className="bg-[#e9ecef] p-4 rounded border">
            <h3 className="text-sm font-semibold mb-3 text-gray-700">
              🚨 Pagamento em atraso (opcional)
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Use esta seção apenas se não conseguiu pagar o valor mínimo dentro do prazo de vencimento da fatura.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block mb-2 font-medium text-sm" htmlFor="dias-atraso">
                  Dias de atraso
                </label>
                <input
                  className="w-full p-2 border rounded"
                  id="dias-atraso"
                  placeholder="0"
                  type="number"
                  min="0"
                  max="30"
                  value={formData.diasAtraso}
                  onChange={(e) => updateFormField('diasAtraso', e.target.value)}
                />
                <p className="text-xs text-gray-600 mt-1">Máximo: 30 dias</p>
              </div>

              <div>
                <label className="block mb-2 font-medium text-sm" htmlFor="multa-atraso">
                  Multa por atraso (%)
                </label>
                <div className="flex border">
                  <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                    %
                  </span>
                  <input
                    className="flex-1 p-2 min-w-[100px]"
                    id="multa-atraso"
                    placeholder="2,0"
                    type="text"
                    value={formData.multaAtraso}
                    onChange={(event) => updateFormField('multaAtraso', sanitizeBrlValue(event))}
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2 font-medium text-sm" htmlFor="juros-mora">
                  Juros de mora (mês)
                </label>
                <div className="flex border">
                  <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                    %
                  </span>
                  <input
                    className="flex-1 p-2 min-w-[100px]"
                    id="juros-mora"
                    placeholder="1,0"
                    type="text"
                    value={formData.jurosMora}
                    onChange={(event) => updateFormField('jurosMora', sanitizeBrlValue(event))}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded border">
            <h3 className="text-sm font-semibold mb-3 text-gray-700">
              Taxas automáticas (Regulamentação Bancária)
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center py-1 border-b border-gray-300">
                <span className="text-gray-600 font-medium">IOF Mensal:</span>
                <span className="text-gray-800">0,38% fixo por lei</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-gray-300">
                <span className="text-gray-600 font-medium">IOF Diário:</span>
                <span className="text-gray-800">0,0082% por dia</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-gray-300">
                <span className="text-gray-600 font-medium">Limite Legal:</span>
                <span className="text-gray-800">100% do valor original</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-600 font-medium">Período Máximo:</span>
                <span className="text-gray-800">30 dias por lei</span>
              </div>
            </div>
          </div>
        </MainCardContent>

        <div className="flex justify-between">
          <Button
            disabled={!isVerified || isCalculating}
            type="submit"
            variant="default"
            className="sm:w-[200px]"
          >
            {isCalculating ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Calculando...
              </>
            ) : (
              "Calcular Rotativo (30 dias)"
            )}
          </Button>
          <Button onClick={clear} variant="ghost" type="button">
            Limpar
          </Button>
        </div>
      </form>
      
      <p className="text-red-800 text-center mt-2 font-bold">{message}</p>
    </MainCard>
  );
};